<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import {
  QuickReplyButtonInput,
  MediaFileInput,
  TemplatePhonePreview,
} from 'dashboard/shared/templates/components';
import {
  useQuickReplyValidation as useSharedQuickReplyValidation,
  useFormValidation,
} from 'dashboard/shared/templates/composables';
import {
  WHATSAPP_TEMPLATE_LIMITS,
  WHATSAPP_MEDIA_ACCEPT,
} from '../constants/whatsappTemplateTypes';
import { useWhatsappConfig } from '../composables/useWhatsappConfig';
import { useWhatsappMediaHeader } from '../composables/useWhatsappMediaHeader';

const props = defineProps({
  buttons: {
    type: Array,
    default: () => [],
  },
  mediaFile: {
    type: File,
    default: null,
  },
  // Onboarding submits templates without the file, so it hides the optional media header.
  allowMedia: {
    type: Boolean,
    default: true,
  },
  body: {
    type: String,
    default: '',
  },
  footer: {
    type: String,
    default: '',
  },
  bodyExamples: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'update:buttons',
  'update:bodyExamples',
  'update:mediaFile',
  'update:header',
  'submit',
  'back',
]);

// Regex for extracting variables
const VARIABLE_PATTERN = /\{\{(\d+)\}\}/g;

const { t } = useI18n();
const { config: whatsappConfig } = useWhatsappConfig();

// Optional media header: when no file is picked the template goes out as text + buttons.
const localFile = ref(props.mediaFile);
const {
  format: mediaFormat,
  previewUrl: mediaPreviewUrl,
  previewMediaType,
} = useWhatsappMediaHeader(localFile);

watch(
  () => props.mediaFile,
  newFile => {
    if (newFile !== localFile.value) {
      localFile.value = newFile;
    }
  }
);

watch(localFile, file => {
  emit('update:mediaFile', file);
  emit('update:header', { format: mediaFormat.value || '' });
});

const localButtons = ref(
  props.buttons.length > 0 ? props.buttons : [{ type: 'QUICK_REPLY', text: '' }]
);

watch(
  () => props.buttons,
  newButtons => {
    if (newButtons.length > 0) {
      localButtons.value = newButtons;
    }
  },
  { deep: true }
);

// Extract variables from body
const detectedVariables = computed(() => {
  const matches = props.body.match(VARIABLE_PATTERN) || [];
  return matches
    .map(m => {
      const num = parseInt(m.replace(/[{}]/g, ''), 10);
      return num;
    })
    .filter((v, i, arr) => arr.indexOf(v) === i) // unique
    .sort((a, b) => a - b);
});

const variablesCount = computed(() => {
  if (detectedVariables.value.length === 0) return 0;
  return Math.max(...detectedVariables.value);
});

// Local copy of bodyExamples
const localExamples = ref([...props.bodyExamples]);

// Sync localExamples with props
watch(
  () => props.bodyExamples,
  newVal => {
    localExamples.value = [...newVal];
  },
  { deep: true }
);

// Watch for variable changes and adjust examples array
watch(variablesCount, newCount => {
  const currentLength = localExamples.value.length;

  if (newCount > currentLength) {
    for (let i = currentLength; i < newCount; i += 1) {
      localExamples.value.push('');
    }
  } else if (newCount < currentLength) {
    localExamples.value = localExamples.value.slice(0, newCount);
  }

  emit('update:bodyExamples', localExamples.value);
});

const handleExampleInput = () => {
  emit('update:bodyExamples', localExamples.value);
};

const getVariableLabel = n => `{{${n}}}`;
const getVariablePlaceholder = n => `Example for {{${n}}}`;

const canAddButton = computed(
  () =>
    localButtons.value.length < WHATSAPP_TEMPLATE_LIMITS.MAX_QUICK_REPLY_BUTTONS
);

const canRemoveButton = computed(() => localButtons.value.length > 1);

// Empty variable validation (WhatsApp Quick Reply doesn't have body variables in this step)
const emptyVariableRules = computed(() => ({}));
const emptyVariableState = computed(() => ({}));

// Use shared Quick Reply validation with WhatsApp config
const {
  rules,
  validationState,
  getErrorMessage: getQrErrorMessage,
} = useSharedQuickReplyValidation(
  localButtons,
  emptyVariableRules,
  emptyVariableState,
  {
    i18nPrefix: 'WHATSAPP_TEMPLATES',
    limits: WHATSAPP_TEMPLATE_LIMITS,
    fieldMapping: { title: 'text' },
    showIdField: false,
  }
);

// Use form validation composable
let v$;
const getErrorMessage = field => getQrErrorMessage(field, v$);

const formValidation = useFormValidation(
  rules,
  validationState,
  getErrorMessage
);
({ v$ } = formValidation);
const { formErrors } = formValidation;

// Submit is disabled when form is invalid
const isSubmitDisabled = computed(() => v$.value.$invalid);

const emitUpdate = () => {
  emit(
    'update:buttons',
    localButtons.value.map(btn => ({
      type: btn.type,
      text: btn.text,
    }))
  );
};

const addButton = event => {
  event?.stopPropagation();
  if (!canAddButton.value) return;

  localButtons.value.push({
    type: 'QUICK_REPLY',
    text: '',
  });
  emitUpdate();
};

const updateButtonField = (index, field, value) => {
  localButtons.value[index][field] = value;
  // Touch the field to show validation
  const fieldKey = `button_${index}_${field}`;
  if (v$.value[fieldKey]) {
    v$.value[fieldKey].$touch();
  }
  emitUpdate();
};

const removeButton = index => {
  if (!canRemoveButton.value) return;

  localButtons.value.splice(index, 1);
  emitUpdate();
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  emitUpdate();
  emit('submit');
};

const handleBack = () => emit('back');

// Preview computed
const previewBody = computed(() => {
  let text = props.body;
  props.bodyExamples.forEach((example, index) => {
    text = text.replace(`{{${index + 1}}}`, example || `[${index + 1}]`);
  });
  return text;
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Info message -->
    <div
      class="text-sm p-3 rounded-lg bg-n-blue/10 text-n-slate-12 border border-n-blue/20"
    >
      <div class="font-medium mb-1">
        {{ t('WHATSAPP_TEMPLATES.CREATE.QUICK_REPLY.INFO_TITLE') }}
      </div>
      <div class="text-n-slate-11">
        {{
          t('WHATSAPP_TEMPLATES.CREATE.QUICK_REPLY.INFO_DESCRIPTION', {
            min: 1,
            max: WHATSAPP_TEMPLATE_LIMITS.MAX_QUICK_REPLY_BUTTONS,
          })
        }}
      </div>
    </div>

    <!-- Main content: Form and Preview side by side -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Left side: Form -->
      <div class="flex flex-col gap-4 order-2 lg:order-1">
        <!-- Optional media header -->
        <div v-if="allowMedia" class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WHATSAPP_TEMPLATES.CREATE.QUICK_REPLY.MEDIA_LABEL') }}
          </label>
          <span class="text-xs text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.CREATE.QUICK_REPLY.MEDIA_HINT') }}
          </span>
          <MediaFileInput
            v-model="localFile"
            :accept="WHATSAPP_MEDIA_ACCEPT"
            i18n-prefix="WHATSAPP_TEMPLATES"
          />
        </div>

        <!-- Quick Reply Buttons -->
        <QuickReplyButtonInput
          v-for="(button, index) in localButtons"
          :key="`button-${index}`"
          :button="button"
          :index="index"
          :form-errors="formErrors"
          :can-remove="canRemoveButton"
          :config="whatsappConfig"
          @update:field="
            (field, value) => updateButtonField(index, field, value)
          "
          @remove="removeButton(index)"
        />

        <!-- Add button -->
        <button
          v-if="canAddButton"
          type="button"
          class="flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-n-slate-7 hover:border-n-blue hover:bg-n-blue/5 text-n-slate-11 hover:text-n-blue transition-colors"
          @click="addButton"
        >
          <i class="i-lucide-plus text-base" />
          <span class="text-sm font-medium">
            {{ t('WHATSAPP_TEMPLATES.CREATE.QUICK_REPLY.ADD_BUTTON') }}
          </span>
        </button>

        <!-- Button count info -->
        <div class="text-sm text-n-slate-11">
          {{
            t('WHATSAPP_TEMPLATES.CREATE.QUICK_REPLY.BUTTON_COUNT', {
              current: localButtons.length,
              max: WHATSAPP_TEMPLATE_LIMITS.MAX_QUICK_REPLY_BUTTONS,
            })
          }}
        </div>

        <!-- Variable Examples -->
        <div v-if="variablesCount > 0" class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WHATSAPP_TEMPLATES.CREATE.FORM.EXAMPLES_LABEL') }}
          </label>
          <span class="text-xs text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.CREATE.FORM.EXAMPLES_HINT') }}
          </span>
          <div class="flex flex-col gap-3">
            <div
              v-for="n in variablesCount"
              :key="n"
              class="flex flex-col gap-1"
            >
              <Input
                v-model="localExamples[n - 1]"
                :label="getVariableLabel(n)"
                :placeholder="getVariablePlaceholder(n)"
                :message="!localExamples[n - 1] ? 'Required' : ''"
                :message-type="!localExamples[n - 1] ? 'error' : 'info'"
                @input="handleExampleInput()"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Right side: Phone Preview -->
      <TemplatePhonePreview
        :body-message="previewBody"
        :preview-placeholder="
          t('WHATSAPP_TEMPLATES.CREATE.QUICK_REPLY.PREVIEW_PLACEHOLDER')
        "
        :title="t('WHATSAPP_TEMPLATES.CREATE.QUICK_REPLY.PREVIEW_TITLE')"
        :media-url="mediaPreviewUrl"
        :media-type="previewMediaType"
        :footer-text="footer"
        :buttons="localButtons"
        button-type="quick_reply"
        button-title-field="text"
        i18n-prefix="WHATSAPP_TEMPLATES"
      />
    </div>

    <!-- Actions -->
    <div class="flex items-center justify-between w-full gap-3 pt-2">
      <Button
        variant="faded"
        color="slate"
        type="button"
        icon="i-lucide-arrow-left"
        :label="t('WHATSAPP_TEMPLATES.CREATE.WIZARD.BACK')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleBack"
      />
      <Button
        :label="t('WHATSAPP_TEMPLATES.CREATE.WIZARD.CREATE')"
        type="button"
        class="w-full"
        :disabled="isSubmitDisabled || loading"
        :is-loading="loading"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
